import { useCallback, useMemo } from "react";
import { Unit } from "../../../../common/types/enums";
import {
  GroupedProduct,
  SaleItemInterface,
  Selector,
  ViewItemInterface,
} from "../../../../common/types/types";
import { unitTypeToShort } from "../../../../common/utils/functions/enumToText";

export const useGroupItem = (group: GroupedProduct) => {
  // 1. Минимальная цена в группе
  const minPrice = useMemo(() => {
    return Math.min(...group.variants.map((v) => v.prices?.[0]?.price || 0));
  }, [group.variants]);

  // 2. Единицы измерения
  const getUnitName = useCallback((unit: Unit): string => {
    return unitTypeToShort(unit) || "";
  }, []);

  // 3. Формируем описание вариантов
  const variantsDescription = useMemo(() => {
    const descriptions: string[] = [];

    Object.values(group.selectors).forEach((selector: Selector) => {
      if (selector.values.length > 0) {
        const unitStr = selector.unit !== -1 ? getUnitName(selector.unit) : "";
        const valuesStr = selector.values
          .map((v: string | number) => `${v}${unitStr}`)
          .join(", ");
        descriptions.push(`${selector.name}: ${valuesStr}`);
      }
    });

    return descriptions.join(" · ");
  }, [group.selectors, getUnitName]);

  // 4. Количество вариантов
  const variantsCount = group.variants.length;

  // 5. Первое изображение (сначала картинка, потом постер, потом null)
  const firstImage = useMemo(() => {
    const allFiles = group.variants.flatMap((v) => v.files || []);

    const imageFile = allFiles.find(
      (path) => !path.includes("video_") && !path.includes("poster_"),
    );
    if (imageFile) return imageFile;

    const posterFile = allFiles.find((path) => path.includes("poster_"));
    if (posterFile) return posterFile;

    return null;
  }, [group.variants]);

  // 6. МИНИМАЛЬНЫЙ РЕЙТИНГ В ГРУППЕ
  const minRating = useMemo(() => {
    const ratings = group.variants
      .map((v) => v.averageRating)
      .filter((r) => typeof r === "number" && !isNaN(r));
    return ratings.length > 0 ? Math.min(...ratings) : undefined;
  }, [group.variants]);

  // 7. ОБЩЕЕ КОЛИЧЕСТВО ОЦЕНОК В ГРУППЕ
  const totalGroupRatings = useMemo(() => {
    return group.variants.reduce(
      (acc, v) =>
        acc + (typeof v.totalRatings === "number" ? v.totalRatings : 0),
      0,
    );
  }, [group.variants]);

  // 8. ОБЩЕЕ КОЛИЧЕСТВО ИЗБРАННОГО В ГРУППЕ
  const totalFavorites = useMemo(() => {
    return group.variants.reduce(
      (acc, v) => acc + (v.selected?.length ? v.selected?.length : 0),
      0,
    );
  }, [group.variants]);

  // 9. ОБЩЕЕ КОЛИЧЕСТВО ПРОСМОТРОВ В ГРУППЕ
  const totalViews = useMemo(() => {
    return group.variants.reduce(
      (acc: ViewItemInterface[], v: SaleItemInterface) => {
        if (v.views && Array.isArray(v.views)) {
          return [...acc, ...v.views];
        }
        return acc;
      },
      [] as ViewItemInterface[],
    );
  }, [group.variants]);

  return {
    minPrice,
    variantsDescription,
    variantsCount,
    firstImage,
    getUnitName,
    minRating,
    totalGroupRatings,
    totalFavorites,
    totalViews,
  };
};
